import type { SpaceResource } from "@app/lib/resources/space_resource";
import { normalizeEgressPolicyDomains } from "@app/types/sandbox/egress_policy";
import type { SandboxEnvVarKind } from "@app/types/sandbox/env_var";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { assertNever } from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType } from "@app/types/user";

// The scope owning a sandbox env var row. The encryption key is derived from
// the same typed object the call site used to fetch the row, so "key matches
// row scope" stays a type-level guarantee. This is NOT an authorization
// point: row scoping in the resources decides access before any decryption
// runs.
export type SandboxEnvVarScope =
  | { kind: "workspace"; workspace: LightWorkspaceType }
  | { kind: "pod"; pod: SpaceResource };

// Values are encrypted under the scope key — workspace sId for workspace
// rows, pod space sId for pod rows. Pod-scoped rows would break if a pod
// ever moved across workspaces with a workspace-derived key; hence the
// per-scope key, NOT always the workspace sId.
export function scopeEncryptionKey(scope: SandboxEnvVarScope): string {
  return scope.kind === "pod" ? scope.pod.sId : scope.workspace.sId;
}

export const SANDBOX_ENV_VAR_PREFIX = "DST_";
const SANDBOX_HTTPS_SECRET_ENV_VAR_PREFIX = "DSEC_";
// Suffix max length is 64 — the wire form is `<prefix><suffix>` (e.g. "DSEC_FOO"),
// so the rendered name fits comfortably under typical 128-char env-var limits
// even with our longest prefix.
export const ENV_VAR_NAME_SUFFIX_REGEX = /^[A-Z][A-Z0-9_]{0,63}$/;
const ENV_VAR_NAME_REGEX = ENV_VAR_NAME_SUFFIX_REGEX;
export const MAX_VALUE_BYTES = 32 * 1_024;
// Tighter than MAX_VALUE_BYTES because https_secret values are substituted
// into HTTP header lines on the wire — most upstreams cap header lines
// around 8-16 KiB. We pick well below the low end to leave headroom for the
// rest of the request line + other headers.
export const MAX_HTTPS_SECRET_VALUE_BYTES = 4 * 1_024;
export const MAX_VARS_PER_WORKSPACE = 50;
export const MAX_VARS_PER_POD = 50;

const ENV_VAR_PREFIX_BY_KIND: Record<SandboxEnvVarKind, string> = {
  config: SANDBOX_ENV_VAR_PREFIX,
  https_secret: SANDBOX_HTTPS_SECRET_ENV_VAR_PREFIX,
};

export function validateEnvVarName(name: string): Result<void, string> {
  if (!ENV_VAR_NAME_REGEX.test(name)) {
    return new Err(
      "Environment variable names must start with A-Z and contain only A-Z, digits or underscores (up to 64 characters)."
    );
  }

  return new Ok(undefined);
}

function validateEnvVarValue(value: string): Result<void, string> {
  if (value.length === 0) {
    return new Err("Environment variable values cannot be empty.");
  }

  if (value.includes("\u0000")) {
    return new Err("Environment variable values cannot contain NUL bytes.");
  }

  if (Buffer.byteLength(value, "utf8") > MAX_VALUE_BYTES) {
    return new Err(
      `Environment variable values cannot exceed ${MAX_VALUE_BYTES / 1_024} KiB.`
    );
  }

  return new Ok(undefined);
}

// Stricter than validateEnvVarValue because https_secret values are
// substituted into HTTP header lines on the wire — embedded CR/LF/NUL/control
// bytes would let an admin smuggle headers via the substituted value.
function validateHttpsSecretValue(value: string): Result<void, string> {
  if (value.length === 0) {
    return new Err("HTTPS secret values cannot be empty.");
  }

  if (/[\u0000-\u001F\u007F]/.test(value)) {
    return new Err("HTTPS secret values cannot contain ASCII control bytes.");
  }

  if (Buffer.byteLength(value, "utf8") > MAX_HTTPS_SECRET_VALUE_BYTES) {
    return new Err(
      `HTTPS secret values cannot exceed ${MAX_HTTPS_SECRET_VALUE_BYTES / 1_024} KiB.`
    );
  }

  return new Ok(undefined);
}

export function validateEnvVarValueForKind({
  kind,
  value,
}: {
  kind: SandboxEnvVarKind;
  value: string;
}): Result<void, string> {
  switch (kind) {
    case "config":
      return validateEnvVarValue(value);
    case "https_secret":
      return validateHttpsSecretValue(value);
    default:
      assertNever(kind);
  }
}

export function envVarPrefixForKind(kind: SandboxEnvVarKind): string {
  return ENV_VAR_PREFIX_BY_KIND[kind];
}

// Domains are compared as sets so reordering the same domains does not count
// as a change (e.g. does not trigger an `allowed_domains_updated` audit
// emission or a no-op row update).
export function areAllowedDomainsEqual(
  left: string[] | null | undefined,
  right: string[] | null | undefined
): boolean {
  const leftSet = new Set(left ?? []);
  const rightSet = new Set(right ?? []);

  if (leftSet.size !== rightSet.size) {
    return false;
  }

  for (const domain of leftSet) {
    if (!rightSet.has(domain)) {
      return false;
    }
  }

  return true;
}

// dsbx rejects the whole secrets table when an exact domain has a single
// label (`localhost`), which kills the forwarder before it binds. The proxy
// allowlist normalizer accepts those, so HTTPS secrets add the rule here.
export function normalizeHttpsSecretAllowedDomains(
  values: string[]
): Result<string[], Error> {
  const normalized = normalizeEgressPolicyDomains(values);
  if (normalized.isErr()) {
    return normalized;
  }

  for (const domain of normalized.value) {
    if (!domain.startsWith("*.") && domain.split(".").length < 2) {
      return new Err(
        new Error(
          `${domain}: HTTPS secret domains need at least two DNS labels separated by a dot, such as github.com or api.github.com.`
        )
      );
    }
  }

  return normalized;
}

export type NormalizedAllowedDomains = string[] | null | undefined;

// Validates the allowedDomains input against the row kind: config rows must
// not carry domains, https_secret rows must carry at least one (unless the
// caller passes none on an update, when `requiredForSecret` is false and the
// stored value is kept — signalled by `undefined`).
export function normalizeAllowedDomainsForKind({
  kind,
  allowedDomains,
  requiredForSecret,
}: {
  kind: SandboxEnvVarKind;
  allowedDomains: string[] | null | undefined;
  requiredForSecret: boolean;
}): Result<NormalizedAllowedDomains, Error> {
  switch (kind) {
    case "config": {
      if (allowedDomains && allowedDomains.length > 0) {
        return new Err(
          new Error("allowedDomains can only be set for HTTPS secrets.")
        );
      }

      return new Ok(null);
    }

    case "https_secret": {
      if (!allowedDomains) {
        if (requiredForSecret) {
          return new Err(
            new Error("HTTPS secrets require at least one allowed domain.")
          );
        }

        return new Ok(undefined);
      }

      if (allowedDomains.length === 0) {
        return new Err(
          new Error("HTTPS secrets require at least one allowed domain.")
        );
      }

      const normalized = normalizeHttpsSecretAllowedDomains(allowedDomains);
      if (normalized.isErr()) {
        return normalized;
      }

      if (normalized.value.length === 0) {
        return new Err(
          new Error("HTTPS secrets require at least one allowed domain.")
        );
      }

      return normalized;
    }

    default:
      assertNever(kind);
  }
}

// Format used both as the agent-visible env var (DSEC_*) for HTTPS secrets
// and as the placeholder field of /run/dust/egress-secrets.json that dsbx
// scans for at MITM time. Both call sites must agree byte-for-byte.
export function renderEgressSecretPlaceholder(nonce: Buffer): string {
  return `__DSEC_${nonce.toString("hex")}__`;
}

export function renderSandboxEnvVarName({
  kind,
  name,
}: {
  kind: SandboxEnvVarKind;
  name: string;
}): string {
  return `${envVarPrefixForKind(kind)}${name}`;
}

// Accepts both prefixed (e.g. "DST_FOO") and suffix-only (e.g. "FOO") inputs.
// - If the input starts with a known prefix, it must match the expected
//   prefix for `kind` (cross-prefix mismatch is rejected).
// - Otherwise the input is validated as a bare suffix and returned as-is.
//   This bare-suffix branch is what lets new-style API clients POST
//   suffix-only names without knowing the prefix convention.
export function parseSandboxEnvVarNameForKind({
  kind,
  name,
}: {
  kind: SandboxEnvVarKind;
  name: string;
}): Result<string, string> {
  const expectedPrefix = envVarPrefixForKind(kind);

  for (const prefix of Object.values(ENV_VAR_PREFIX_BY_KIND)) {
    if (name.startsWith(prefix)) {
      if (prefix !== expectedPrefix) {
        return new Err(
          `Environment variable name ${name} has prefix ${prefix}, expected ${expectedPrefix}.`
        );
      }

      const suffix = name.slice(prefix.length);
      const suffixValidation = validateEnvVarName(suffix);
      if (suffixValidation.isErr()) {
        return suffixValidation;
      }

      return new Ok(suffix);
    }
  }

  // Bare-suffix branch: no recognized prefix, validate the raw input as a
  // suffix and return it.
  const validation = validateEnvVarName(name);
  if (validation.isErr()) {
    return validation;
  }

  return new Ok(name);
}
